import RefreshIcon from '@mui/icons-material/Refresh';
import {Link} from '@mui/material';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useState} from 'react';
import ReactJson from 'react-json-view';
import {useSelector} from 'react-redux';

import CustomTable from '../../../components/CustomTable/CustomTable';
import ToolBar from '../../../components/ToolBar/ToolBar';
import {ConfigConstant} from '../../../constants/config';
import RoutesConstant from '../../../constants/routes';
import {fetchBulkOutletSyncHistory} from '../../../service/Outlet';
import {pxToRem} from '../../../utils';
import {convertObjectToURLParams, formatChannelCode} from '../../../utils/helper';
import {StyledChip} from '../../Operation/OperationsRaw';

import BulkOutletSyncForm from './BulkOutletSyncForm';

const StyledLink = styled(Link)(({theme}) => ({
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}));

const StyledTypography = styled(Typography)(({theme}) => ({
  fontSize: pxToRem(32),
  fontWeight: 'bold',
  color: theme.palette.grey[700],
}));

const BulkOutletSync = () => {
  const [globalFilter, setGlobalFilter] = useState('');
  const permissions = useSelector((state) => state.permissions);

  const dataQuery = useQuery({
    queryKey: ['data-bulk-outlet'],
    queryFn: () => fetchBulkOutletSyncHistory(),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleRedirectToRaw = (row) => {
    const defaultFilters = {
      date: [
        new Date(new Date(row?.timestamp).setHours(0, 0, 0)).toISOString(),
        new Date(new Date(row?.timestamp).setHours(23, 59, 59)).toISOString(),
      ],
      operationType: [ConfigConstant.MONGO_CONFIG?.OPERATION_TYPES?.['STORE_TOGGLE']?.code],
      channelCode: [formatChannelCode(row)],
      rebelCorrelationId: row?.rebelCorrelationId,
    };
    const queryParams = convertObjectToURLParams(defaultFilters);
    window.open(`${RoutesConstant.OPS_RAW}?${queryParams}`, '_blank');
  };
  const columns = [
    {
      id: 'Date',
      header: 'Date',
      filterable: false,
      accessorFn: (row) =>
        `${format(new Date(row.timestamp), 'dd MMM')} ${format(new Date(row.timestamp), 'HH:mm:ss')}`,
    },
    {
      id: 'rebelCorrelationId',
      header: 'rebelCorrelationId',
      cell: ({row}) => {
        return (
          <StyledLink onClick={() => handleRedirectToRaw(row?.original)}>
            {row?.original?.rebelCorrelationId}
          </StyledLink>
        );
      },
      filterable: false,
    },
    {
      id: 'Applied Filters',
      header: 'Applied Filters',
      cell: ({row}) => {
        const channelName =
          row?.original?.channelCode === row?.original?.channelGroupCode
            ? `${row?.original?.channelName}-direct`
            : `${row?.original?.channelGroupName}-${row?.original?.channelName}`;
        const data = {
          channel: channelName,
          country: row?.original?.countryName,
          ...(row?.original?.city?.length && {city: row?.original?.city}),
          ...(row?.original?.partnerBrandName?.length && {brand: row?.original?.partnerBrandName}),
          ...(row?.original?.partnerPhyStoreName?.length && {physicalStore: row?.original?.partnerPhyStoreName}),
          ...(row?.original?.partnerStoreCode?.length && {virtualStoreCode: row?.original?.partnerStoreCode}),
        };
        return <ReactJson src={data} name={false} displayDataTypes={false} collapsed={true} />;
      },
    },
    {
      id: 'Client',
      header: 'Client',
      accessorKey: 'request.client',
      filterable: false,
    },
    {
      id: 'User',
      header: 'User',
      accessorKey: 'request.user',
      filterable: false,
    },
    {
      id: 'status',
      header: 'status',
      cell: ({row}) => {
        return <StyledChip label={row.original?.status} />;
      },
      filterable: false,
    },
    {
      id: 'Message',
      header: 'Message',
      accessorKey: 'response.data.message',
      filterable: false,
    },
  ];
  const refetchSyncHistory = () => {
    dataQuery.refetch();
  };
  const refreshBtnProps = {
    button: true,
    tooltip: false,
    text: 'Refresh',
    icon: RefreshIcon,
    onClick: () => refetchSyncHistory(),
  };
  return (
    <>
      {(permissions.admin || permissions.bulkOutletSync) && (
        <>
          <StyledTypography>Bulk Outlet Status Sync</StyledTypography>
          <br />
          <BulkOutletSyncForm refetchSyncHistory={refetchSyncHistory} />
        </>
      )}
      <ToolBar
        headerText={
          permissions.admin || permissions.bulkOutletSync
            ? `Last ${ConfigConstant.MONGO_CONFIG?.BULK_STORE_STATUS_SYNC_PAGESIZE} Transactions`
            : `Bulk Outlet Sync History`
        }
        showGlobalSearch={true}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        showLegends={false}
        showExportButton={{button: false, tooltip: false}}
        showCustomButtons={[refreshBtnProps]}
        headerFontSize={permissions.admin || permissions.bulkOutletSync ? 32 : ''}
      />
      <CustomTable
        tableKey={'outlets-status-sync'}
        tableHeight={permissions.admin || permissions.bulkOutletSync ? 48 : 84}
        columns={columns}
        dataQuery={dataQuery}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        enablePagination={false}
        allowDateRangeSelection={false}
      />
    </>
  );
};

export default BulkOutletSync;
